import React, { lazy, Suspense, useContext } from 'react';
import { DWTLoadable, DWTLoadableComponentPart, wrapAsDefault } from '../common/components/loader/dwt-loadable';
import { environmentSvc } from '../common/environment.service';
import { ExternalName } from '../common/general/module/brand-loader';
import { textDirectionGetter } from '../common/styles/general/text-direction';
import { ModuleContext } from './context/module-context';
import { DWTComponentOptions } from './dwt-app';
import { createRoot } from 'react-dom/client';

const ModuleContextProvider = lazy(() =>
  import('./context/module-context-provider').then((m) => m.ModuleContextProvider).then(wrapAsDefault)
);
const EmbedRoot = lazy(() => import('../embedding/embed-root').then((m) => m.EmbedRoot).then(wrapAsDefault));

export const DwtModuleComponent = () => {
  const moduleContext = useContext(ModuleContext);
  if (!moduleContext) return null;
  const DwtModule = DWTLoadable(moduleContext.module.entryPoint);
  return <DwtModule />;
};

export const DwtModuleComponentPart = (props: any) => {
  const moduleContext = useContext(ModuleContext);
  if (!moduleContext) return null;
  const DwtModule = DWTLoadableComponentPart(moduleContext.module.entryPoint);
  return <DwtModule {...props} />;
};
export const renderApp = (moduleName: ExternalName, element: Element, options: DWTComponentOptions) => {
  const brand = options.brand === 'admin' ? 'admin' : options.brand;
  const root = createRoot(element);
  root.render(
    <Suspense fallback={null}>
      <ModuleContextProvider
        brand={brand}
        customData={options.customData}
        widgetData={options.widgetData}
        moduleName={moduleName}
        adminMode={(options.adminMode as unknown as string)?.toLowerCase?.() === 'true' || options.adminMode === true}
      >
        <EmbedRoot options={options} moduleName={moduleName} directionGetter={textDirectionGetter(element)}>
          <DwtModuleComponent />
        </EmbedRoot>
      </ModuleContextProvider>
    </Suspense>
  );
};

export const RenderComponent = (moduleName: ExternalName, options: DWTComponentOptions) => {
  return (
    <ModuleContextProvider brand={options.brand} widgetData={options.widgetData} moduleName={moduleName}>
      <EmbedRoot options={options} moduleName={moduleName} directionGetter={textDirectionGetter(document.body)}>
        <DwtModuleComponentPart />
      </EmbedRoot>
    </ModuleContextProvider>
  );
};

export async function renderModule(moduleName: ExternalName, element: Element, options: DWTComponentOptions) {
  environmentSvc.devMode = !!options.devMode || environmentSvc.devMode;

  let root = element;
  if (element.hasChildNodes()) {
    const appContainer = document.createElement('div');
    element.appendChild(appContainer);
    root = appContainer;
  }

  renderApp(moduleName, root, options);
}
